import { useEffect, useMemo, useRef, useState } from "react";
import { Tooltip, Input, Tag, AutoComplete, message, Typography } from "antd";
import { TweenOneGroup } from "rc-tween-one";

const { Text } = Typography;

const normalizeTags = (rawTags) =>
  Array.from(
    new Set(
      (Array.isArray(rawTags) ? rawTags : [])
        .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
        .filter(Boolean)
    )
  );

export default function FaqTagsEditor({
  value = [],
  onChange,
  editable = false,
  disabled = false,
  suggestedTags = [],
}) {
  const [tags, setTags] = useState(normalizeTags(value));
  const [inputVisible, setInputVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [editInputWidth, setEditInputWidth] = useState(180);
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);

  useEffect(() => {
    setTags(normalizeTags(value));
  }, [value]);

  useEffect(() => {
    if (!editable) return;
    if (inputVisible || editingIndex !== null) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [inputVisible, editingIndex, editable]);

  useEffect(() => {
    if (!editable || editingIndex === null) return;

    const handleDocClick = (e) => {
      const target = e.target;
      if (inputContainerRef.current && inputContainerRef.current.contains(target)) return;
      if (target && target.closest && target.closest("[data-faq-tag='true']")) return;
      cancelEdit();
    };

    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, [editingIndex, editable]);

  const publishTags = (nextTags) => {
    const cleaned = normalizeTags(nextTags);
    setTags(cleaned);
    onChange?.(cleaned);
  };

  const showAddInput = () => {
    if (!editable || disabled) return;
    setInputVisible(true);
    setInputValue("");
    setEditingIndex(null);
  };

  const cancelEdit = () => {
    setInputValue("");
    setInputVisible(false);
    setEditingIndex(null);
  };

  const handleAddOrSave = (rawValue = inputValue) => {
    const trimmed = rawValue?.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }

    let nextTags;
    if (editingIndex !== null) {
      nextTags = [...tags];
      nextTags[editingIndex] = trimmed;
    } else {
      if (tags.includes(trimmed)) {
        message.warning("Category already exists");
        return;
      }
      nextTags = [...tags, trimmed];
    }

    publishTags(nextTags);
    setInputValue("");
    setInputVisible(false);
    setEditingIndex(null);
  };

  const autocompleteOptions = useMemo(() => {
    const normalizedSuggestions = Array.from(
      new Set(
        (Array.isArray(suggestedTags) ? suggestedTags : [])
          .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
          .filter(Boolean)
      )
    );

    const currentEditingTag =
      editingIndex !== null ? tags[editingIndex] : null;
    const normalizedInput = (inputValue || "").trim().toLowerCase();

    return normalizedSuggestions
      .filter((tag) => {
        if (tag === currentEditingTag) return true;
        return !tags.includes(tag);
      })
      .filter((tag) => {
        if (!normalizedInput) return true;
        return tag.toLowerCase().includes(normalizedInput);
      })
      .map((tag) => ({ value: tag }));
  }, [editingIndex, inputValue, suggestedTags, tags]);

  const handleRemove = (removedTag) => {
    if (!editable || disabled) return;
    publishTags(tags.filter((tag) => tag !== removedTag));
  };

  const handleEdit = (tag, index, e) => {
    if (!editable || disabled) return;
    e.stopPropagation();
    const tagElement = e.currentTarget?.closest?.(".ant-tag");
    if (tagElement) {
      const width = Math.max(80, Math.ceil(tagElement.getBoundingClientRect().width));
      setEditInputWidth(width);
    } else {
      setEditInputWidth(180);
    }
    setEditingIndex(index);
    setInputVisible(false);
    setInputValue(tag);
  };

  const renderTag = (tag, index) => {
    const tagKey = `tag-${tag}`;
    if (editable && editingIndex === index) {
      return (
        <div
          key={tagKey}
          style={{ display: "inline-block", marginBottom: 4 }}
          ref={inputContainerRef}
        >
          <AutoComplete
            options={autocompleteOptions}
            value={inputValue}
            onChange={setInputValue}
            onSelect={(selectedValue) => handleAddOrSave(selectedValue)}
            style={{ width: editInputWidth }}
            getPopupContainer={(node) => node.parentElement || document.body}
          >
            <Input
              ref={inputRef}
              size="small"
              onPressEnter={() => handleAddOrSave()}
              onBlur={() => {
                setTimeout(() => {
                  if (
                    editingIndex !== null &&
                    inputContainerRef.current &&
                    !inputContainerRef.current.contains(document.activeElement)
                  ) {
                    cancelEdit();
                  }
                }, 0);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          </AutoComplete>
        </div>
      );
    }

    const displayText = tag.length > 35 ? `${tag.slice(0, 35)}...` : tag;

    return (
      <span
        key={tagKey}
        style={{ display: "inline-block", marginBottom: 4 }}
        data-faq-tag="true"
      >
        <Tooltip title={tag}>
          <Tag
            closable={editable}
            onClose={(e) => {
              e.preventDefault();
              if (disabled) return;
              handleRemove(tag);
            }}
            style={{
              cursor: editable ? "pointer" : "default",
              borderColor: "rgba(22, 119, 255, 0.35)",
              background: "rgba(22, 119, 255, 0.12)",
              color: "rgba(22, 119, 255, 0.95)",
              borderRadius: 8,
              fontWeight: 500,
              fontSize: 13,
              padding: "2px 8px",
              lineHeight: "20px",
            }}
          >
            <span
              onDoubleClick={(e) => handleEdit(tag, index, e)}
              data-faq-tag="true"
            >
              {displayText}
            </span>
          </Tag>
        </Tooltip>
      </span>
    );
  };

  const tagPlusStyle = {
    background: "rgba(140, 140, 140, 0.12)",
    border: "1px dashed rgba(89, 89, 89, 0.45)",
    color: "rgba(89, 89, 89, 0.95)",
    cursor: "pointer",
    width: "auto",
    height: 26,
    padding: "0 10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "24px",
    fontSize: 13,
    borderRadius: 8,
    fontWeight: 500,
  };

  if (!tags.length && !editable) {
    return <Text type="secondary">No category</Text>;
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      <TweenOneGroup
        appear={false}
        enter={{ opacity: 1, scale: 1, duration: 0 }}
        leave={{ opacity: 0, width: 0, scale: 0, duration: 200 }}
      >
        {tags.map((tag, index) => renderTag(tag, index))}
      </TweenOneGroup>

      {editable && !disabled && !inputVisible && editingIndex === null && (
        <Tooltip title="Add Category">
          <Tag onClick={showAddInput} style={tagPlusStyle}>
            + Add Category
          </Tag>
        </Tooltip>
      )}

      {editable && !disabled && inputVisible && editingIndex === null && (
        <div ref={inputContainerRef}>
          <AutoComplete
            options={autocompleteOptions}
            value={inputValue}
            onChange={setInputValue}
            onSelect={(selectedValue) => handleAddOrSave(selectedValue)}
            style={{ width: 180 }}
            getPopupContainer={(node) => node.parentElement || document.body}
          >
            <Input
              ref={inputRef}
              size="small"
              onPressEnter={() => handleAddOrSave()}
              onBlur={() => {
                setTimeout(() => {
                  if (
                    !inputContainerRef.current ||
                    !inputContainerRef.current.contains(document.activeElement)
                  ) {
                    setInputVisible(false);
                    setInputValue("");
                  }
                }, 0);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          </AutoComplete>
        </div>
      )}
    </div>
  );
}
